import { NgZone, OnDestroy, inject, Service } from '@angular/core';
import { Router } from '@angular/router';
import { ExtendedGetResult } from '@fingerprintjs/fingerprintjs-pro';
import { BehaviorSubject, Observable, Subject, of, throwError } from 'rxjs';
import { catchError, first, map, mergeMap, takeUntil } from 'rxjs/operators';
import { Api, ApiClient, ApiTypes, httpStatus } from '../../api';
import { CONSTANTS } from '../../common/constants/constants';
import { CONSTANTS_ERRORS } from '../../common/constants/errors/errors.constants';
import { CONSTANTS_ROUTING } from '../../common/constants/routing/routing.constants';
import { Languages } from '../../common/enums/languages.enum';
import { IQuestionPayload, QuestionType } from '../../core/interfaces/general.interface';
import { FingerprintService } from '../../core/providers/fingerprint/fingerprint.service';
import { DEMOGRAPHICS_CONSTANTS } from '../demographics/demographics.constants';
import { DemographicsService } from '../demographics/demographics.service';
import { IDemographicsDto } from '../demographics/interfaces/demographics-dto.interface';
import { FLEXIBLE_PORTAL_CONSTANTS } from '../flexible-portal/flexible-portal.constants';
import { WHEEL_OF_FORTUNE_ROUTING_CONSTANTS } from '../wheel-of-fortune/wheel-of-fortune-routing.constants';
import { WheelOfFortuneService } from '../wheel-of-fortune/wheel-of-fortune.service';
import { toCampaign } from './campaign.mapper';
import {
  CampaignAnswerValue,
  ICampaign,
  ICampaignDraft,
  ICampaignDraftAnswer,
  ICampaignFormValue,
  ICampaignOptions,
  ICampaignPayload,
  ICampaignQuestion,
} from './interfaces/campaign.interface';

const RESPONSE_NOT_SAVED_MESSAGE =
  'The campaign response has not been saved, so there is nothing to attach the demographics to.';

function isCampaignDraft(value: unknown): value is ICampaignDraft {
  return (
    typeof value === 'object' &&
    value !== null &&
    'savedAt' in value &&
    typeof value.savedAt === 'number' &&
    'answers' in value &&
    Array.isArray(value.answers)
  );
}

@Service()
export class CampaignService implements OnDestroy {
  private router = inject(Router);
  private fingerprintService = inject(FingerprintService);
  private zone = inject(NgZone);
  private demographicsService = inject(DemographicsService);
  private wheelOfFortuneService = inject(WheelOfFortuneService);
  private api = inject(ApiClient);

  /** In-progress campaign answers expire after this idle window. */
  private readonly draftTtlMs: number = 30 * 60 * 1000;

  currentIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  questionLimitIndex = 0;
  currentIndex = 0;
  previousQuestionIndexes: number[] = [];
  private token: string | null = null;
  private campaign: ICampaign | null = null;
  private onDestroy$: Subject<void> = new Subject<void>();
  private responseUuid: string | null = null;

  backToPreviousQuestion(): void {
    const previousIndex: number | undefined = this.previousQuestionIndexes.pop();

    if (previousIndex === undefined) {
      this.goToPortal();
    } else {
      this.currentIndex = previousIndex;
      this.currentIndex$.next(previousIndex);
    }
  }

  /** Emits `null` once a 404 or 403 has already sent the consumer to its page. */
  fetchCampaign$(
    venueUuid: string,
    visitorId: string
  ): Observable<ICampaign | ApiTypes.CampaignSurveyTokenResponseDto | null> {
    return this.api
      .get(Api.surveys.takeaway.campaign.venues, {
        pathParams: { venueUuid },
        fingerprint: visitorId,
      })
      .pipe(
        map(response => ('token' in response ? response : toCampaign(response))),
        catchError((error: unknown) => {
          switch (httpStatus(error)) {
            case CONSTANTS_ERRORS.HTTP_404: {
              this.zone.run(() => void this.router.navigate([CONSTANTS_ROUTING.ROUTES.ERROR]));

              return of(null);
            }
            case CONSTANTS_ERRORS.HTTP_403: {
              this.zone.run(
                () =>
                  void this.router.navigate([
                    `${CONSTANTS_ROUTING.ROUTES.ADD.MAIN}/${CONSTANTS_ROUTING.ROUTES.ADD.BACK}`,
                  ])
              );

              return of(null);
            }
            default:
              break;
          }

          return throwError(() => error);
        })
      );
  }

  /** Emits `null` once a 404 has already sent the consumer to the error page. */
  fetchExactCampaign$(
    venueId: string,
    campaignId: string,
    visitorId: string
  ): Observable<ICampaign | null> {
    return this.api
      .get(Api.surveys.takeaway.campaign.venues.campaigns, {
        pathParams: { venueUuid: venueId, campaignUuid: campaignId },
        fingerprint: visitorId,
      })
      .pipe(
        map(toCampaign),
        catchError((error: unknown) => {
          switch (httpStatus(error)) {
            case CONSTANTS_ERRORS.HTTP_404: {
              this.zone.run(() => void this.router.navigate([CONSTANTS_ROUTING.ROUTES.ERROR]));

              return of(null);
            }
            default:
              break;
          }

          return throwError(() => error);
        })
      );
  }

  /** Emits `null` once a 404 or 500 has already sent the consumer to its page. */
  saveCampaign(
    payload: ICampaignPayload
  ): Observable<ApiTypes.CampaignResponseAcceptedResponseDto | null> {
    return this.fingerprintService.visitorId$().pipe(
      first(),
      mergeMap(visitorId =>
        this.api
          .post(Api.surveys.takeaway.campaign.response, {
            body: { ...payload, visitorId },
          })
          .pipe(
            catchError((error: unknown) => {
              switch (httpStatus(error)) {
                case CONSTANTS_ERRORS.HTTP_404: {
                  void this.router.navigate([CONSTANTS_ROUTING.ROUTES.NOT_FOUND]);

                  return of(null);
                }
                case CONSTANTS_ERRORS.HTTP_500: {
                  void this.router.navigate([CONSTANTS_ROUTING.ROUTES.ERROR]);

                  return of(null);
                }
                default:
                  break;
              }

              return throwError(() => error);
            })
          )
      )
    );
  }

  goToNextStep(question: ICampaignQuestion, formValue: CampaignAnswerValue): void {
    const nextStep: number | null = this.getNextStep(question, formValue);

    if (nextStep) {
      this.loadQuestion(nextStep);
    }
  }

  /** The 1-based step after `question`, or `null` when it is the last one. */
  getNextStep(question: ICampaignQuestion, formValue: CampaignAnswerValue): number | null {
    if (!question.isCustomNextStep) {
      const questionIndex: number = this.getQuestionIndexByUuid(question.uuid);

      return questionIndex + 1 < this.getQuestions().length ? questionIndex + 2 : null;
    }

    return this.getCustomNextStep(question, formValue);
  }

  setPortalToken(token: string): void {
    this.token = token;
    localStorage.setItem(CONSTANTS.LOCALSTORAGE_KEYS.PORTAL_TOKEN, token);
  }

  setCampaign(campaign: ICampaign): void {
    this.campaign = campaign;
  }

  /** `null` until a campaign has been set, since there is no campaign to answer yet. */
  getCampaignPayload(
    venueUuid: string,
    answers: IQuestionPayload[],
    lang: Languages
  ): ICampaignPayload | null {
    if (!this.campaign) {
      return null;
    }

    return {
      venueUuid,
      answers,
      lang,
      campaignUuid: this.campaign.campaign.uuid,
    };
  }

  redirectAfterSave(responseUuid: string): void {
    this.responseUuid = responseUuid;
    const campaign: ICampaign | null = this.campaign;

    if (!campaign) {
      this.goToFinalPage();

      return;
    }

    if (campaign.info.wheel) {
      this.wheelOfFortuneService.setWheelConfig(campaign.info.wheel);

      if (campaign.info.wheel.isActive) {
        this.wheelOfFortuneService.campaignResponseUuid = responseUuid;
        this.wheelOfFortuneService.campaignUuid = campaign.campaign.uuid;
      }
    }
    if (
      campaign.demographics.isEnabled &&
      (campaign.demographics.shouldAskForAgeGroup || campaign.demographics.shouldAskForGender)
    ) {
      this.demographicsService.setDemographicsConfig(campaign.demographics);
      this.demographicsService.setGenderOptionsSource({
        campaignUuid: campaign.campaign.uuid,
      });
      this.demographicsService.isCampaignMode = true;
      this.goToDemographicsForm();
    } else {
      this.goToHappyWheelOrAdvertisement();
    }
  }

  goToFinalPage(): void {
    void this.router.navigate([
      `${CONSTANTS_ROUTING.ROUTES.ADD.MAIN}/${CONSTANTS_ROUTING.ROUTES.ADD.THANK_YOU}`,
    ]);
  }

  goToHappyWheelAfterDemographicsCanceling(demographicsDto: IDemographicsDto): void {
    this.saveSurveyDemographics$(demographicsDto).pipe(takeUntil(this.onDestroy$)).subscribe();

    if (this.wheelOfFortuneService.getWheelConfig()?.isDemographicsEnabled) {
      void this.router.navigate([
        `${CONSTANTS_ROUTING.ROUTES.ADD.MAIN}/${CONSTANTS_ROUTING.ROUTES.ADD.THANK_YOU}`,
      ]);
    } else {
      this.goToHappyWheelOrAdvertisement();
    }
  }

  saveDemographics(demographicsDto: IDemographicsDto): void {
    this.saveSurveyDemographics$(demographicsDto)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => this.goToHappyWheelOrAdvertisement());
  }

  /**
   * Persist the respondent's in-progress answers so the campaign can be resumed
   * after a reload. Stored in `sessionStorage` (scoped to the tab/session, so it
   * cannot leak to the next guest on a shared device) and keyed by question/
   * option uuid (so it stays small and survives server-side campaign edits).
   */
  saveCampaignAnswers(formValue: ICampaignFormValue, campaignId: string): void {
    const answers: ICampaignDraftAnswer[] = formValue.questions
      .map((question: ICampaignQuestion) => this.extractDraftAnswer(question))
      .filter((answer: ICampaignDraftAnswer | null): answer is ICampaignDraftAnswer => !!answer);

    if (!answers.length) {
      sessionStorage.removeItem(campaignId);

      return;
    }

    const draft: ICampaignDraft = { savedAt: Date.now(), answers };

    try {
      sessionStorage.setItem(campaignId, JSON.stringify(draft));
    } catch {
      // Drafting is best-effort — ignore quota / private-mode failures.
    }
  }

  getCampaignAnswers(campaignId: string): ICampaignDraftAnswer[] | null {
    let draft: unknown = null;

    try {
      const raw: string | null = sessionStorage.getItem(campaignId);

      draft = raw ? JSON.parse(raw) : null;
    } catch {
      draft = null;
    }

    if (!isCampaignDraft(draft)) {
      return null;
    }

    if (Date.now() - draft.savedAt > this.draftTtlMs) {
      sessionStorage.removeItem(campaignId);

      return null;
    }

    return draft.answers;
  }

  removeStorage(campaignId: string): void {
    sessionStorage.removeItem(campaignId);
  }

  private extractDraftAnswer(question: ICampaignQuestion): ICampaignDraftAnswer | null {
    const options: ICampaignOptions[] = question.options ?? [];
    const selectedOptionUuids: string[] = options
      .filter((option: ICampaignOptions) => option.value && option.uuid)
      .map((option: ICampaignOptions) => option.uuid);
    const optionTexts: { optionUuid: string; text: string }[] = options.flatMap(
      (option: ICampaignOptions) =>
        option.uuid && option.text ? [{ optionUuid: option.uuid, text: option.text }] : []
    );
    const value: string | undefined =
      typeof question.value === 'string' && question.value !== '' ? question.value : undefined;

    if (!selectedOptionUuids.length && !optionTexts.length && value === undefined) {
      return null;
    }

    return {
      questionUuid: question.uuid,
      ...(selectedOptionUuids.length ? { selectedOptionUuids } : {}),
      ...(optionTexts.length ? { optionTexts } : {}),
      ...(value !== undefined ? { value } : {}),
    };
  }

  sendFirstFingerprintDataAnalytics(
    campaignUuid: string,
    data: ExtendedGetResult,
    isBlocked: boolean,
    checkInUuid?: string
  ) {
    const fingerprintDTO: ApiTypes.StoreFingerprintCommand = {
      campaignUuid,
      data: { ...data },
      isBlocked,
      checkInUuid,
    };

    return this.api.post(Api.surveys.takeaway.fingerprint, { body: fingerprintDTO });
  }

  sendFingerprintDataAnalytics(
    campaignResponseUuid: string,
    data: ExtendedGetResult,
    isBlocked: boolean,
    campaignUuid: string,
    checkInUuid?: string
  ) {
    const fingerprintDTO: ApiTypes.StoreFingerprintCommand = {
      campaignResponseUuid,
      data: { ...data },
      isBlocked,
      checkInUuid,
      campaignUuid,
    };

    return this.api.post(Api.surveys.takeaway.fingerprint, { body: fingerprintDTO });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private saveSurveyDemographics$(demographicsDto: IDemographicsDto) {
    const responseUuid: string | null = this.responseUuid;
    const request$ = responseUuid
      ? this.api.post(Api.surveys.takeaway.campaign.demographics, {
          body: { ...demographicsDto, responseUuid },
        })
      : throwError(() => new Error(RESPONSE_NOT_SAVED_MESSAGE));

    return request$.pipe(
      catchError((error: unknown) => {
        this.zone.run(() => void this.router.navigate([CONSTANTS_ROUTING.ROUTES.ERROR]));

        return throwError(() => error);
      })
    );
  }

  private goToHappyWheelOrAdvertisement(): void {
    if (
      this.campaign?.info.wheel?.isActive &&
      this.wheelOfFortuneService.isAgeGroupAllowedForWheelSpin()
    ) {
      void this.router.navigate([WHEEL_OF_FORTUNE_ROUTING_CONSTANTS.ROUTES.HAPPY_WHEEL.MAIN]);
    } else {
      this.goToFinalPage();
    }
  }

  private goToDemographicsForm(): void {
    const wheel = this.campaign?.info.wheel;

    if (wheel?.isDemographicsEnabled && wheel.isActive) {
      void this.router.navigate([
        `${DEMOGRAPHICS_CONSTANTS.ROUTES.MAIN}/${DEMOGRAPHICS_CONSTANTS.ROUTES.FORMS.WHEEL_OF_FORTUNE}`,
      ]);
    } else {
      void this.router.navigate([DEMOGRAPHICS_CONSTANTS.ROUTES.MAIN]);
    }
  }

  private getCustomNextStep(question: ICampaignQuestion, formValue: CampaignAnswerValue): number {
    if (question.type === QuestionType.Text || question.type === QuestionType.Date) {
      return this.getQuestionIndexByUuid(question.nextQuestion) + 1;
    }
    const selectedIndex: number = (formValue.options ?? []).findIndex((option: boolean) => option);
    const selectedOption: ICampaignOptions | undefined = question.options?.[selectedIndex];

    return this.getQuestionIndexByUuid(selectedOption?.nextQuestion) + 1;
  }

  private getQuestions(): ICampaignQuestion[] {
    return this.campaign?.campaign.questions ?? [];
  }

  private getQuestionIndexByUuid(uuid: string | null | undefined): number {
    return uuid
      ? this.getQuestions().findIndex(
          (campaignQuestion: ICampaignQuestion) => campaignQuestion.uuid === uuid
        )
      : -1;
  }

  private loadQuestion(step: number): void {
    this.previousQuestionIndexes.push(this.currentIndex);
    this.currentIndex = step - 1;
    this.currentIndex$.next(this.currentIndex);
  }

  private goToPortal(): void {
    if (this.token) {
      void this.router.navigate([`${FLEXIBLE_PORTAL_CONSTANTS.ROUTES.MAIN}/${this.token}`]);
    }
  }
}
